import type { CSSProperties } from 'react';
import type { AvatarSlot } from '../../../lib/avatar-color';
import styles from './Avatar.module.css';

/**
 * The DS avatar's `Color` variant prop. `'brand'` is the logged-in user's own green gradient — the
 * one identity that is not "some person", so it is not one of the ramp's slots. Anything else is a
 * slot off `AVATAR_PALETTE`, normally obtained with `avatarSlotFor(<a stable seed>)`.
 */
export type AvatarColor = 'brand' | AvatarSlot;

/**
 * The DS size scale — Figma `Symphony Avatar` (270:3663) ships 18 / 20 / 24 / 30(→32px) / 40 / 48.
 *
 * `size` is a plain number rather than a union of those six, because two call sites are deliberately
 * off-scale and both are written down rather than silently tolerated:
 *   • `FileCreatedBy` renders at 12–16 — an inline attribution mark sized to the text line it sits on,
 *     not a DS avatar; forcing it up to 18 would break the files-table row rhythm.
 *   • the DESKTOP Account card renders at 72 — a hero the DS has no frame for (every Settings frame in
 *     Figma is the 390px mobile one, at 40).
 * New call sites should pick from the scale above.
 */
export const AVATAR_DS_SIZES = [18, 20, 24, 32, 40, 48] as const;

export interface AvatarProps {
  /** The person's initials — the fallback whenever there is no usable photo. */
  initials: string;
  /** The photo, or null. Already resolved by the caller (e.g. `useProfilePhoto`), which owns the decision. */
  photoUrl?: string | null;
  /** Called when the photo 404s / fails to decode, so the caller can fall back to the initials. */
  onPhotoError?: () => void;
  /** Diameter in px. See `AVATAR_DS_SIZES`. */
  size: number;
  /** DS `Color`. Defaults to the user's brand gradient — pass a slot for anyone who is not the user. */
  color?: AvatarColor;
  className?: string;
}

/**
 * THE avatar. One component for every circular person mark in the app, so the same person cannot
 * appear as a photo in one place and a differently-shaped, differently-coloured disc in another.
 *
 * It carries all three of the Figma component's variant props:
 *   • **Type** — `Image` when a `photoUrl` resolves, `Initials` otherwise (and on photo error).
 *   • **Size** — `size`, per `AVATAR_DS_SIZES`.
 *   • **Color** — `color`: the user's brand gradient, or one of the DS `--ds-avatar-*` slots.
 *
 * The initials' type scale is DERIVED from `size` (see `glyphPx`) rather than passed in. It used to be
 * a required `initialsVariant` prop that every caller had to pick correctly for its size, and they
 * disagreed — the 40px card asked for 16px initials where the DS asks for 12.
 *
 * Decorative by construction: the photo carries an empty `alt` and the whole circle is `aria-hidden`,
 * because every call site already renders the name it stands for.
 */
export function Avatar({ initials, photoUrl, onPhotoError, size, color = 'brand', className }: AvatarProps) {
  const slot = color === 'brand' ? null : color;
  return (
    <span
      aria-hidden
      className={[styles.root, slot ? styles.slot : styles.brand, className].filter(Boolean).join(' ')}
      style={
        {
          '--avatar-size': `${size}px`,
          '--avatar-glyph': `${glyphPx(size)}px`,
          ...(slot ? { '--avatar-fill': `var(${slot.bg})`, '--avatar-ink': `var(${slot.fg})` } : {}),
        } as CSSProperties
      }
    >
      {photoUrl ? <img src={photoUrl} alt="" onError={onPhotoError} className={styles.photo} /> : initials}
    </span>
  );
}

/**
 * The initials' font size for a given diameter, per the DS: every avatar from 24 up uses
 * `Tiny/Semi Bold` (12/16) and everything below it `Super Tiny/Semi Bold` (10/16) — the glyph does NOT
 * scale with the circle across the designed range, which is why a 40px avatar's initials look small.
 *
 * Above the scale there is no DS value to follow, so it stays proportional-ish at the 20px the desktop
 * Account hero already rendered (the old `h2`), keeping that one undesigned surface where it was.
 */
function glyphPx(size: number): number {
  if (size > 48) return 20;
  if (size >= 24) return 12;
  return 10;
}
